// Copyright (c) 2020-2021, 2024 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { withStyles } from "tss-react/mui";
import { RouteComponentProps, Switch, Route, RouteProps, Redirect } from 'react-router-dom';
import { observer } from 'mobx-react';

import Logo from '@shared/icons/Logo';
import ROUTES from '@shared/constants/routes';
import Lazyload from '@shared/components/LazyLoad';
import VersionTag from '@shared/components/VersionTag';
import TowerImage from './assets/VI_Login_Hero Image_Tower.jpg';
import MapImage from './assets/map.png';

// Lazy load the public pages.
const Login = React.lazy(() => import('./submodules/Login'));
const ForgotPassword = React.lazy(() => import('./submodules/ForgotPassword'));
const NewPassword = React.lazy(() => import('./submodules/NewPassword'));
const AccountActivate = React.lazy(() => import('./submodules/AccountActivate'));
const QRCode = React.lazy(() => import('./submodules/QRCode'))

export interface PublicProps extends RouteComponentProps {
  className?: string;
  classes?: Record<string, string>;
}

@observer
// Set up the routing table for public pages.
class Public extends React.Component<PublicProps> {
  get routes(): Array<RouteProps> {
    const { login, forgotPassword, newPassword, accountActivate, qrCode } = ROUTES.public;

    return [
      { path: login, component: Login },
      { path: forgotPassword, component: ForgotPassword },
      { path: newPassword, component: NewPassword },
      { path: accountActivate, component: AccountActivate },
      { path: qrCode, component: QRCode },
    ];
  }

  render() {
    const classes = withStyles.getClasses(this.props);

    return (
      <div className={classes.root}>
        <Logo classes={{ root: classes.logo }} />
        <div className={classes.form}>
          <Lazyload>
            <Switch>
              {this.routes.map((routeProps) => <Route key={String(routeProps.path)} {...routeProps} />)}
              <Redirect to={ROUTES.public.login} />
            </Switch>
          </Lazyload>
        </div>
        <VersionTag />
        <div className={classes.img} />
      </div>
    );
  }
}

const PublicStyled = withStyles(
  Public, 
  (theme, props) => ({
    "root": {
      display: 'flex',
      justifyContent: 'space-between',
      height: '100%',
    },
    "logo": {
      position: 'absolute' as const,
      top: 40,
      left: 40,
      width: 106,
      height: 'auto',
    },
    "form": {
      width: '50%',
      flexDirection: 'column' as const,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundImage: `linear-gradient(to bottom, rgba(255, 255, 255, .005), rgba(255, 255, 255, .005)), url(${MapImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'bottom right' as const
    }, 
    "img": {
      width: '50%',
      backgroundImage: `linear-gradient(to bottom, #00000000, #00000000), url(${TowerImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center' as const,
    },
  })
);

export default PublicStyled;